/**
 * A box an image is drawn in, and the screens it is drawn in it on.
 */
export type ImageBox = {
  /**
   * The screens this box is for: `(min-width: 768px)`. The last box has none and is every
   * other screen, so list them widest screen first, the order `sizes` reads them in.
   */
  media?: string;
  /** How wide the image is drawn, in CSS pixels. */
  width: number;
  /**
   * How tall, for a box the image fills and is cropped to from the top, as `object-cover
   * object-top` draws it. Without it the image is drawn in its own shape.
   */
  height?: number;
};

const DENSITIES = [1, 2, 3];

/**
 * `url` for an `<img srcset sizes>`: at the size each box draws it, and at twice and three
 * times that, so a screen of any density loads what it needs and no more.
 *
 * Never bigger than the image is. Asked for more, the resizer scales it up, and the file
 * grows with nothing to show for it: a book's cover is 400px tall, and a phone would load
 * three times the original to draw it. A box the image cannot fill at some density gets
 * the whole of it there instead. `width` and `height` are the image's own.
 */
export function srcSet(url: string, width: number, height: number, boxes: ImageBox[]) {
  const candidates = new Map<number, string>();
  let src = url;

  boxes.forEach((box) => {
    // How much the box scales the image at 1x. One it fills covers both ways.
    const scale = box.height ? Math.max(box.width / width, box.height / height) : box.width / width;

    DENSITIES.forEach((density) => {
      const factor = Math.min(density, 1 / scale);
      const candidateWidth = pixels(box.width * factor);
      const candidate = resized(url, candidateWidth, box.height && pixels(box.height * factor));

      if (!candidates.has(candidateWidth)) {
        candidates.set(candidateWidth, candidate);
      }

      if (!box.media && density === 1) {
        src = candidate;
      }
    });
  });

  return {
    src,
    srcSet: [...candidates]
      .sort(([a], [b]) => a - b)
      .map(([candidateWidth, candidate]) => `${candidate} ${candidateWidth}w`)
      .join(', '),
    sizes: boxes.map((box) => (box.media ? `${box.media} ${box.width}px` : `${box.width}px`)).join(', '),
  };
}

/**
 * `url` at `width` wide, from the resizer it is on — cropped from the top to `height` when
 * there is one.
 *
 * The resizer reads our images from our bucket by their path, which takes the size as its
 * query. Two kinds it has to be handed whole, as a url to fetch: a package's cover under
 * `/packs/`, which the web server composes, and a url with a query already, which is a
 * resized one — the digital frame.
 */
export function resized(url: string, width: number, height?: number): string {
  const size = height
    ? `auto=format%2Ccompress&crop=top&cs=strip&fit=crop&h=${height}&w=${width}`
    : `auto=format%2Ccompress&cs=strip&w=${width}`;
  const origin = url.match(/^https?:\/\/[^/]+/)?.[0] ?? '';
  const path = url.slice(origin.length);

  if (path.includes('?') || path.startsWith('/packs/')) {
    return `${origin}/${encodeURIComponent(url)}?${size}`;
  }

  // A file name arrives as stored, and a space in one would end the url inside `srcset`.
  return `${origin}${path.split('/').map(encodeURIComponent).join('/')}?${size}`;
}

/**
 * Whole pixels, never fewer than drawn. Rounded first, or floating point asks for a pixel
 * more: a book 308 wide, in a box 218 wide, comes to 308.00000000000006 at its own size.
 */
function pixels(value: number): number {
  return Math.ceil(Math.round(value * 100) / 100);
}
